<template>
  <div>
    <AthleteSectionHeading class="mb-5">
      Contact
    </AthleteSectionHeading>
    <div class="flex flex-wrap p-4 mb-8 overflow-hidden bg-white rounded md:p-6 card">
      <div
        v-if="(user.twitterHandle || user.instagramHandle) && isLoggedOut"
        class="relative w-full"
      >
        <div class="flex flex-row gap-2 absolute right-0 top-0">
          <a
            :href="`https://x.com/${user.twitterHandle}`"
            target="_blank"
            :class="[isLoggedOut ? 'ds-text-2xl text-blue-300' : '']"
          >
            <i class="sr-2 sr-v2-twitter" />
          </a>
          <a
            :href="`https://instagram.com/${user.instagramHandle}`"
            target="_blank"
            :class="[isLoggedOut ? 'ds-text-2xl text-blue-300' : '']"
          >
            <i class="sr-2 sr-v2-instagram" />
          </a>
        </div>
      </div>
      <div class="w-full p-0 md:pr-4 md:w-1/3 lg:w-1/4">
        <div class="m-0 mb-2 md:mb-4">
          <UserAvatar
            :first-name="user.firstName"
            :last-name="user.lastName"
            :size="96"
            :image-url="profilePhotoUrl"
            :is-logged-out="isLoggedOut"
            class="block md:hidden"
          />
          <UserAvatar
            :first-name="user.firstName"
            :last-name="user.lastName"
            :size="160"
            :image-url="profilePhotoUrl"
            :is-logged-out="isLoggedOut"
            class="hidden md:block"
          />
        </div>
        <div class="mt-2 md:mt-4">
          <AthleteField
            v-if="user.emailAddress"
            label="email"
            class="break-all md:mb-3"
          >
            <a
              :href="`mailto:${user.emailAddress}`"
              class="mb-1 font-normal text-blue-300 lowercase ds-text-base"
            >{{ user.emailAddress }}</a>
          </AthleteField>

          <AthleteField
            v-if="user.phoneNumber"
            label="phone"
            class="md:mb-3"
          >
            <a
              :href="`tel:${user.phoneNumber}`"
              class="mb-1 font-normal text-blue-300 ds-text-base"
            >{{ formatPhoneNumber(user.phoneNumber) }}</a>
          </AthleteField>

          <div class="flex flex-row gap-2">
            <div
              v-if="user.twitterHandle && !isLoggedOut"
              class="relative"
            >
              <a
                :href="`https://x.com/${user.twitterHandle}`"
                target="_blank"
                :class="[isLoggedOut ? '' : 'ds-text-2xl text-blue-300']"
              >
                <i class="sr-2 sr-v2-twitter" />
              </a>
            </div>

            <div
              v-if="user.instagramHandle && !isLoggedOut"
              class="relative"
            >
              <a
                :href="`https://instagram.com/${user.instagramHandle}`"
                target="_blank"
                :class="[isLoggedOut ? '' : 'ds-text-2xl text-blue-300']"
              >
                <i class="sr-2 sr-v2-instagram" />
              </a>
            </div>
          </div>

          <div
            v-if="showMessageButton"
            class="w-full mt-4"
          >
            <a
              :href="`/messages/compose?athlete=${user.id}`"
              :target="shouldOpenMessagesInNewTab && '_blank'"
            >
              <sr-button text="Send Message" />
            </a>
          </div>

          <LoginBoxCTA
            v-if="isLoggedOut"
            class="hidden text-center md:inline-block"
          />
        </div>
      </div>
      <div
        class="flex flex-wrap w-full p-0 mt-6 md:mt-0 md:pl-4 md:w-2/3 lg:w-3/4 lg:flex-grow-1 h-auto"
      >
        <div class="w-full lg:w-8/12 lg:h-full">
          <h3 class="m-0 mb-2 ds-text-2xl font-bold text-gray-1000">
            Personal Statement
          </h3>

          <div v-if="user.bio">
            <MaxHeightExpandingContent
              :max-height="144"
              class="mb-6 md:hidden"
            >
              <p
                class="max-w-full overflow-x-hidden font-normal text-gray-900 whitespace-pre-wrap ds-text-base AthleteContactInformation-athlete-bio md:ml-6 md:mb-6"
              >
                {{ user.bio }}
              </p>
            </MaxHeightExpandingContent>
            <p
              class="hidden max-w-full overflow-x-hidden font-normal text-gray-900 whitespace-pre-wrap ds-text-base md:block AthleteContactInformation-athlete-bio md:mb-6"
            >
              {{ user.bio }}
            </p>
          </div>
          <AthleteBioEmptyState v-else />
        </div>

        <LoginBoxCTA
          v-if="isLoggedOut"
          class="w-full text-center md:hidden"
        />

        <div
          class="hidden w-full bg-gray-300 h-1px"
          :class="[ isLoggedOut ? 'hidden' : 'md:block lg:hidden' ]"
        />

        <div
          class="flex flex-wrap w-full p-0 lg:w-4/12 lg:block lg:pt-10 lg:pl-4 md:mt-6 lg:mt-0 guardian-list"
        >
          <div
            v-for="(guardian, index) in user.guardians"
            :key="guardian.emailAddress"
            class="w-full md:w-1/2 lg:w-full lg:px-0"
            :class="[index % 2 ? 'md:pl-2' : 'md:pr-2']"
          >
            <AthleteField
              v-if="guardian.firstName || guardian.lastName"
              :label="`Guardian ${index +1} name`"
              class="md:mb-3"
            >
              <p
                class="mb-1 font-semibold text-gray-900 ds-text-base"
              >
                {{ guardian.firstName }} {{ guardian.lastName }}
              </p>
            </AthleteField>

            <AthleteField
              v-if="guardian.emailAddress"
              :label="`Guardian ${index +1} email`"
              class="md:mb-3"
            >
              <a
                :href="`mailto:${guardian.emailAddress}`"
                class="max-w-full mb-1 font-normal text-blue-300 lowercase break-all ds-text-base"
              >{{ guardian.emailAddress }}</a>
            </AthleteField>

            <AthleteField
              v-if="guardian.phoneNumber"
              :label="`Guardian ${index +1} phone`"
              class="md:mb-3"
            >
              <a
                :href="`tel:${guardian.phoneNumber}`"
                class="mb-1 font-normal text-blue-300 ds-text-base"
              >{{ formatPhoneNumber(guardian.phoneNumber) }}</a>
            </AthleteField>

            <div
              v-if="index < user.guardians.length -1"
              class="w-full my-4 bg-gray-300 h-1px md:hidden lg:block"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AthleteField from "../../AthleteField.vue";
import AthleteSectionHeading from "../../AthleteSectionHeading.vue";
import UserAvatar from "@/components/UserAvatar";
import { mapState, mapGetters } from "vuex";
import AthleteBioEmptyState from "./AthleteBioEmptyState.vue";
import LoginBoxCTA from "../../OTG/LoginBoxCTA.vue";
import { Button as SrButton } from "@sportsrecruits/playbook";
import MaxHeightExpandingContent from "../MaxHeightExpandingContent.vue";
import formatPhoneNumber from "@/utils/formatters/phoneNumber";

export default {
    components: {
        AthleteField,
        AthleteSectionHeading,
        UserAvatar,
        AthleteBioEmptyState,
        LoginBoxCTA,
        SrButton,
        MaxHeightExpandingContent,
    },
    props: {
        showMessageButton: { type: Boolean, required: false, default: true },
    },
    methods: {
        formatPhoneNumber,
    },
    computed: {
        ...mapState({
            user: state => state.athleteProfile.user,
        }),
        ...mapGetters({
            profilePhotoUrl: "athleteProfile/userProfilePhotoUrl",
            fullName: "athleteProfile/userFullName",
            isLoggedOut: "userData/isLoggedOut",
            isUsingProfileKey: "userData/isUsingProfileKey",
            isCollegeCoach: "athleteProfile/isApplicableCollegeCoach",
        }),

        shouldOpenMessagesInNewTab() {
          return !this.isUsingProfileKey
        }
    },
};
</script>

<style lang="scss">
.AthleteContactInformation-athlete-bio {
    p {
        font-size: 1rem !important;
        font-family: "Inter", helvetica, arial, sans-serif !important;
        margin-bottom: 1rem !important;
    }

    p:last-of-type {
        margin-bottom: 0 !important;
    }
}
.guardian-list {
    & > div:last-child > div:last-child {
        margin-bottom: 0px !important;
    }
}
</style>
